/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from '@packages/utils';
import { useGetEventByIdQuery } from 'redux/apiSlice/events.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { RoutesPaths } from 'routes/Routes.types';
import EventStatus from 'components/Shared/Statuses/EventStatus';
import PageWrapper from 'components/Base/PageWrapper';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { Tabs } from 'components/Base/Tabs';
import { EEventStatus } from 'data/types/events.types';
import { TRtkErrorType } from 'data/types/generic.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import { useEventAccessControl } from 'hooks/useEventAccessControl';
import { useAppSelector } from 'redux/store';
import ReadOnlyWrapper from 'components/Base/ReadOnlyWrapper';
import Notes from 'components/Shared/Notes';
import { ENoteEntityType } from 'data/types/notes.types';
import OutputTab from 'components/Shared/OutputTab';
import Actions from './Actions';
import DetailsTab from './DetailsTab';
import WaitListTab from './WaitlistTab';
import { EWorkShopDetailsTabsUrl } from './EventDetails.types';
import ChildCareTab from './ChildCareTab';
import QuestionTab from './QuestionTab';
import RegistrantsTab from './RegistrantsTab';
import AttendanceTab from './AttendanceTab';
import { EAttendanceType } from '../../../../../data/types/workshops.types';

const WorkShopDetails = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events',
  });
  const { showToastNotification } = useShowToastNotification();
  const params = useParams();
  const location = useLocation();
  const goBackState = (location.state as TBackButtonState) || {
    backUrl: `/app/${RoutesPaths.EVENTS}`,
    backButtonSuffix: t('list.TITLE'),
  };
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const { id } = params as { id: string };
  const { data, isLoading, isFetching, error } = useGetEventByIdQuery({
    id,
  });
  const usersData = useAppSelector(state => state.userSlice);
  const { canEdit } = useEventAccessControl({
    userTypes: usersData?.user?.type,
    userId: usersData?.user?.userId,
    eventData: data,
  });

  useEffect(() => {
    if (location.search.includes('duplicated')) {
      showToastNotification({
        message: t('details.actions.duplicate.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      searchParams.delete('eventAction');
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
  }, [location.search]);

  const onBackClick = () => {
    navigate(goBackState.backUrl);
  };

  const isOutputsRoute = location.pathname.endsWith('/inputs-outputs');
  const { onTabChange, tabInUrl } = useKeepTabsInUrl({
    defaultTab: isOutputsRoute
      ? EWorkShopDetailsTabsUrl.OUTPUT
      : EWorkShopDetailsTabsUrl.DETAILS,
    stateInUrl: goBackState,
  });

  const handleTabChange = (key: string) => {
    if (!id) return;
    if (key === EWorkShopDetailsTabsUrl.OUTPUT) {
      navigate(
        `/app/${RoutesPaths.EVENTS_BY_ID}/inputs-outputs`.replace(':id', id),
      );
    } else {
      const basePath = `/app/${RoutesPaths.EVENTS_BY_ID}`.replace(':id', id);
      navigate(`${basePath}?tab=${key}`, { state: goBackState });
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: EWorkShopDetailsTabsUrl.DETAILS,
      label: <span data-cy="details_tab">{t('details.tabs.DETAILS')}</span>,
      children: (
        <DetailsTab
          data={data}
          isFetching={isFetching}
          id={id}
          canEdit={canEdit}
        />
      ),
    },
    // {
    //   key: EWorkShopDetailsTabsUrl.REGISTRANTS,
    //   label: (
    //     <span data-cy="registrants_tab">{t('details.tabs.REGISTRANTS')}</span>
    //   ),
    //   children: <RegistrantsTab data={data} />,
    //   show: data?.status !== EEventStatus.PLANNING,
    // },
    {
      key: EWorkShopDetailsTabsUrl.ATTENDANCE,
      label: (
        <span data-cy="attendance_tab">{t('details.tabs.ATTENDANCE')}</span>
      ),
      children: (
        <ReadOnlyWrapper isReadOnly={!canEdit}>
          <AttendanceTab data={data} />
        </ReadOnlyWrapper>
      ),
      show:
        data?.requiresRegistration && data?.status !== EEventStatus.PLANNING,
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.WAITLIST,
      label: <span data-cy="waitlist_tab">{t('details.tabs.WAITLIST')}</span>,
      children: (
        <ReadOnlyWrapper isReadOnly={!canEdit}>
          <WaitListTab data={data} />
        </ReadOnlyWrapper>
      ),
      show:
        data?.requiresRegistration && data?.status !== EEventStatus.PLANNING,
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.QUESTION,
      label: <span data-cy="question_tab">{t('details.tabs.QUESTION')}</span>,
      children: (
        <ReadOnlyWrapper isReadOnly={!canEdit}>
          <QuestionTab question={data?.customQuestion} eventId={id} />
        </ReadOnlyWrapper>
      ),
      show: !!(
        (data?.status === EEventStatus.PLANNING &&
          !data.customQuestion.allowedOperations.readonly) ||
        data?.customQuestion?.value
      ),
    },
    {
      key: EWorkShopDetailsTabsUrl.CHILD_CARE,
      label: (
        <span data-cy="childcare_tab">{t('details.tabs.CHILD_CARE')}</span>
      ),
      children: (
        <ReadOnlyWrapper isReadOnly={!canEdit}>
          <ChildCareTab id={id} />
        </ReadOnlyWrapper>
      ),
      show: !!(
        data?.status !== EEventStatus.PLANNING && data?.childcareProvided?.value
      ),
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.NOTES,
      label: <span data-cy="notes_tab">{t('details.tabs.NOTES')}</span>,
      children: <Notes entityType={ENoteEntityType.EVENT} entityId={id} />,
    },
    {
      key: EWorkShopDetailsTabsUrl.OUTPUT,
      label: (
        <span data-cy="output_tab">{t('details.tabs.OUTPUT', 'Outputs')}</span>
      ),
      children: (
        <ReadOnlyWrapper isReadOnly={!canEdit}>
          <OutputTab
            id={id}
            hostId={data?.host?.id}
            locationId={data?.location?.id}
            isVirtual={data?.attendanceType === EAttendanceType.VIRTUAL}
            sourceType="EVENT"
          />
        </ReadOnlyWrapper>
      ),
    },
  ];

  return (
    <PageWrapper
      title={data?.host.name}
      titleSuffix={<EventStatus className="ml-2" text={data?.status} />}
      {...(data && {
        backButton: {
          labelPrefix: data?.host.name,
          tooltip: goBackState.backButtonSuffix,
          onClick: onBackClick,
        },
      })}
      actions={<Actions id={id} data={data} />}
      isLoading={isLoading}
      error={data ? undefined : (error as TRtkErrorType)}
    >
      <Tabs
        defaultActiveKey={tabInUrl}
        onChange={handleTabChange}
        items={tabItems}
        destroyInactiveTabPane
      />
    </PageWrapper>
  );
};

export default WorkShopDetails;
